import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FiPlus, FiX, FiCheck, FiClock, FiAlertCircle, FiTrendingUp,
  FiAlertTriangle, FiStar, FiZap, FiTrash2, FiEdit2, FiFilter, FiRefreshCw,
  FiPackage, FiCalendar, FiTag
} from 'react-icons/fi';

const API_URL = getApiUrl();

// Bug icon component since FiBug doesn't exist
const BugIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.88 1.88" />
    <path d="M14.12 3.88L16 2" />
    <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
    <path d="M12 20v-9" />
    <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
    <path d="M6 13H2" />
    <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
    <path d="M22 13h-4" />
    <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </svg>
);

const TYPE_CONFIG = {
  feature: { label: 'Nouveauté', color: 'bg-emerald-500', icon: FiStar },
  bugfix: { label: 'Correction', color: 'bg-red-500', icon: FiAlertTriangle },
  improvement: { label: 'Amélioration', color: 'bg-blue-500', icon: FiZap },
  breaking: { label: 'Breaking Change', color: 'bg-purple-500', icon: FiAlertCircle }
};

const STATUS_CONFIG = {
  planned: { label: 'Planifié', color: 'bg-slate-400' },
  in_progress: { label: 'En cours', color: 'bg-amber-500' },
  completed: { label: 'Terminé', color: 'bg-emerald-500' },
  cancelled: { label: 'Annulé', color: 'bg-red-300' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'text-slate-500' },
  medium: { label: 'Moyenne', color: 'text-amber-600' },
  high: { label: 'Haute', color: 'text-orange-600' },
  critical: { label: 'Critique', color: 'text-red-600' }
};

const BUG_STATUS_CONFIG = {
  open: { label: 'Ouvert', color: 'bg-red-500' },
  in_progress: { label: 'En cours', color: 'bg-amber-500' },
  resolved: { label: 'Résolu', color: 'bg-emerald-500' },
  closed: { label: 'Fermé', color: 'bg-slate-400' },
  wontfix: { label: "Won't Fix", color: 'bg-slate-300' }
};

export default function BacklogPage() {
  const [activeTab, setActiveTab] = useState('changelog');
  const [versionInfo, setVersionInfo] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Snapshots/Rollback states
  const [snapshots, setSnapshots] = useState([]);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [restoringSnapshot, setRestoringSnapshot] = useState(null);
  const [snapshotName, setSnapshotName] = useState('');
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [versionRes, changelogRes, bugsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/versioning/current`),
        fetch(`${API_URL}/api/versioning/changelog`),
        fetch(`${API_URL}/api/versioning/bugs`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API_URL}/api/versioning/stats`)
      ]);

      if (versionRes.ok) setVersionInfo(await versionRes.json());
      if (changelogRes.ok) setChangelog(await changelogRes.json());
      if (bugsRes.ok) setBugs(await bugsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Load snapshots
      const snapshotsRes = await fetch(`${API_URL}/api/versioning/snapshots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (snapshotsRes.ok) setSnapshots(await snapshotsRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create a new snapshot
  const handleCreateSnapshot = async () => {
    if (!snapshotName.trim()) {
      toast.error('Veuillez entrer un nom pour le snapshot');
      return;
    }
    
    setCreatingSnapshot(true);
    try {
      const response = await fetch(`${API_URL}/api/versioning/snapshots`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          name: snapshotName,
          version: versionInfo?.version || '2.18.0'
        })
      });

      if (response.ok) {
        toast.success('Snapshot créé avec succès');
        setShowSnapshotModal(false);
        setSnapshotName('');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création du snapshot');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setCreatingSnapshot(false);
    }
  };

  // Restore a snapshot
  const handleRestoreSnapshot = async (snapshotId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir restaurer cette version ? Les données actuelles seront remplacées.')) {
      return;
    }
    
    setRestoringSnapshot(snapshotId);
    try {
      const response = await fetch(`${API_URL}/api/versioning/snapshots/${snapshotId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Version restaurée avec succès ! Rechargement de la page...');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la restauration');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setRestoringSnapshot(null);
    }
  };

  // Delete a snapshot
  const handleDeleteSnapshot = async (snapshotId) => {
    if (!window.confirm('Supprimer ce snapshot ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/versioning/snapshots/${snapshotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Snapshot supprimé');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveChangelog = async (entry) => {
    try {
      const method = entry.id && !entry.id.startsWith('default') ? 'PUT' : 'POST';
      const url = method === 'PUT' 
        ? `${API_URL}/api/versioning/changelog/${entry.id}`
        : `${API_URL}/api/versioning/changelog`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(entry)
      });

      if (response.ok) {
        toast.success(method === 'PUT' ? 'Entrée mise à jour' : 'Entrée ajoutée');
        setShowAddModal(false);
        setEditItem(null);
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSaveBug = async (bug) => {
    try {
      const method = bug.id ? 'PUT' : 'POST';
      const url = method === 'PUT' 
        ? `${API_URL}/api/versioning/bugs/${bug.id}`
        : `${API_URL}/api/versioning/bugs`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(bug)
      });

      if (response.ok) {
        toast.success(method === 'PUT' ? 'Bug mis à jour' : 'Bug signalé');
        setShowAddModal(false);
        setEditItem(null);
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Supprimer cet élément ?')) return;

    try {
      const endpoint = type === 'changelog' ? 'changelog' : 'bugs';
      const response = await fetch(`${API_URL}/api/versioning/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Supprimé');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredChangelog = changelog.filter(entry => {
    if (filterType !== 'all' && entry.type !== filterType) return false;
    if (filterStatus !== 'all' && entry.status !== filterStatus) return false;
    return true;
  });

  const filteredBugs = bugs.filter(bug => {
    if (filterStatus !== 'all' && bug.status !== filterStatus) return false;
    return true;
  });

  const groupedChangelog = filteredChangelog.reduce((acc, entry) => {
    const version = entry.version || 'Non versionné';
    if (!acc[version]) acc[version] = [];
    acc[version].push(entry);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backlog-page">
      {/* Header with Version Info */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Backlog & Changelog</h1>
          <p className="text-slate-600 mt-1">Suivi des versions, bugs et nouveautés</p>
        </div>
        <div className="flex items-center gap-4">
          {versionInfo && (
            <div className="text-right">
              <div className="flex items-center gap-2">
                <FiPackage className="h-5 w-5 text-indigo-600" />
                <span className="text-2xl font-bold text-indigo-600">v{versionInfo.version}</span>
              </div>
              <p className="text-sm text-slate-500">
                Déployé le {new Date(versionInfo.deployed_at || versionInfo.release_date).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })} à {new Date(versionInfo.deployed_at || versionInfo.release_date).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
          <Button onClick={() => { setEditItem(null); setShowAddModal(true); }}>
            <FiPlus className="mr-2 h-4 w-4" />
            {activeTab === 'changelog' ? 'Nouvelle entrée' : 'Signaler un bug'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <FiStar className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Nouveautés</p>
                  <p className="text-2xl font-bold">{stats.changelog?.features || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <BugIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bugs ouverts</p>
                  <p className="text-2xl font-bold">{stats.bugs?.open || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiZap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Améliorations</p>
                  <p className="text-2xl font-bold">{stats.changelog?.improvements || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <FiCheck className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bugs résolus</p>
                  <p className="text-2xl font-bold">{stats.bugs?.resolved || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('changelog')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'changelog' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FiTrendingUp className="inline mr-2 h-4 w-4" />
          Changelog ({changelog.length})
        </button>
        <button
          onClick={() => setActiveTab('bugs')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'bugs' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <BugIcon className="inline mr-2 h-4 w-4" />
          Bugs ({bugs.length})
        </button>
        <button
          onClick={() => setActiveTab('versions')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'versions' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FiPackage className="inline mr-2 h-4 w-4" />
          Historique versions
        </button>
      </div>

      {/* Version History Tab */}
      {activeTab === 'versions' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FiPackage className="h-5 w-5 text-indigo-600" />
                  Historique & Rollback
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Créez des points de sauvegarde et restaurez une version antérieure de votre contenu
                </p>
              </div>
              <Button onClick={() => setShowSnapshotModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <FiPlus className="mr-2 h-4 w-4" />
                Créer un snapshot
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Current Version */}
            <div className="mb-6 p-4 rounded-lg border-2 border-indigo-200 bg-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                    <FiPackage className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">v{versionInfo?.version || '2.10.0'}</span>
                      <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                        VERSION ACTUELLE
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      Déployé le {versionInfo?.deployed_at 
                        ? new Date(versionInfo.deployed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
                        : '08 mars 2026'} à {versionInfo?.deployed_at 
                        ? new Date(versionInfo.deployed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : '17:00'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Snapshots List */}
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FiClock className="h-4 w-4" />
              Points de sauvegarde ({snapshots.length})
            </h3>
            
            {snapshots.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <FiPackage className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-2">Aucun snapshot créé</p>
                <p className="text-sm text-slate-400">Créez un snapshot pour pouvoir restaurer votre contenu plus tard</p>
              </div>
            ) : (
              <div className="space-y-3">
                {snapshots.map((snapshot) => (
                  <div 
                    key={snapshot.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center">
                        <FiPackage className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{snapshot.name}</span>
                          <span className="text-xs text-slate-400">v{snapshot.version}</span>
                        </div>
                        <p className="text-sm text-slate-500">
                          {new Date(snapshot.created_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRestoreSnapshot(snapshot.id)}
                        disabled={restoringSnapshot === snapshot.id}
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      >
                        {restoringSnapshot === snapshot.id ? (
                          <FiRefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <FiRefreshCw className="h-4 w-4 mr-1" />
                            Restaurer
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteSnapshot(snapshot.id)}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Comment fonctionne le rollback ?</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Un snapshot sauvegarde l'état complet de votre contenu (pages, paramètres, médias). 
                    La restauration remplace le contenu actuel par celui du snapshot sélectionné.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Snapshot Creation Modal */}
      <AnimatePresence>
        {showSnapshotModal && (
          <Dialog open={showSnapshotModal} onOpenChange={setShowSnapshotModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer un snapshot</DialogTitle>
                <DialogDescription>
                  Sauvegardez l'état actuel de votre contenu pour pouvoir le restaurer plus tard
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Nom du snapshot</Label>
                  <Input
                    value={snapshotName}
                    onChange={(e) => setSnapshotName(e.target.value)}
                    placeholder="Ex: Avant mise à jour SEO"
                    className="mt-1"
                  />
                </div>
                <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                  <p><strong>Version actuelle :</strong> v{versionInfo?.version || '2.10.0'}</p>
                  <p><strong>Date :</strong> {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSnapshotModal(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateSnapshot} 
                  disabled={creatingSnapshot}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {creatingSnapshot ? (
                    <>
                      <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <FiCheck className="mr-2 h-4 w-4" />
                      Créer le snapshot
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>

      {/* Filters - Only show for changelog and bugs tabs */}
      {activeTab !== 'versions' && (
      <div className="flex gap-4 items-center">
        <FiFilter className="h-4 w-4 text-slate-400" />
        {activeTab === 'changelog' && (
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
          >
            <option value="all">Tous les types</option>
            <option value="feature">Nouveautés</option>
            <option value="bugfix">Corrections</option>
            <option value="improvement">Améliorations</option>
          </select>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
        >
          <option value="all">Tous les statuts</option>
          {activeTab === 'changelog' ? (
            <>
              <option value="completed">Terminé</option>
              <option value="in_progress">En cours</option>
              <option value="planned">Planifié</option>
            </>
          ) : (
            <>
              <option value="open">Ouvert</option>
              <option value="in_progress">En cours</option>
              <option value="resolved">Résolu</option>
              <option value="closed">Fermé</option>
            </>
          )}
        </select>
        <Button variant="ghost" size="sm" onClick={loadData}>
          <FiRefreshCw className="h-4 w-4" />
        </Button>
      </div>
      )}

      {/* Content */}
      {activeTab === 'changelog' ? (
        <div className="space-y-8">
          {Object.entries(groupedChangelog).sort((a, b) => b[0].localeCompare(a[0])).map(([version, entries]) => (
            <div key={version}>
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <FiTag className="h-5 w-5 text-indigo-600" />
                Version {version}
              </h3>
              <div className="space-y-3">
                {entries.map((entry) => {
                  const TypeIcon = TYPE_CONFIG[entry.type]?.icon || FiStar;
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${TYPE_CONFIG[entry.type]?.color || 'bg-slate-500'} text-white`}>
                            <TypeIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{entry.title}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[entry.status]?.color || 'bg-slate-400'} text-white`}>
                                {STATUS_CONFIG[entry.status]?.label || entry.status}
                              </span>
                            </div>
                            {entry.description && (
                              <p className="text-sm text-slate-600 mt-1">{entry.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <FiCalendar className="h-3 w-3" />
                                {new Date(entry.created_at).toLocaleDateString('fr-FR')}
                              </span>
                              <span className={PRIORITY_CONFIG[entry.priority]?.color || ''}>
                                {PRIORITY_CONFIG[entry.priority]?.label || entry.priority}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => { setEditItem(entry); setShowAddModal(true); }}
                            className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(entry.id, 'changelog')}
                            className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
          {filteredChangelog.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Aucune entrée dans le changelog
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBugs.map((bug) => (
            <motion.div
              key={bug.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${BUG_STATUS_CONFIG[bug.status]?.color || 'bg-slate-400'} text-white`}>
                    <BugIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-900">{bug.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${BUG_STATUS_CONFIG[bug.status]?.color || 'bg-slate-400'} text-white`}>
                        {BUG_STATUS_CONFIG[bug.status]?.label || bug.status}
                      </span>
                      <span className={`text-xs font-medium ${PRIORITY_CONFIG[bug.priority]?.color || ''}`}>
                        {PRIORITY_CONFIG[bug.priority]?.label || bug.priority}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{bug.description}</p>
                    {bug.resolution && (
                      <div className="mt-2 p-2 bg-emerald-50 rounded-lg text-sm text-emerald-700">
                        <strong>Résolution:</strong> {bug.resolution}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>v{bug.version_reported}</span>
                      {bug.version_fixed && (
                        <span className="text-emerald-600">Corrigé en v{bug.version_fixed}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <FiCalendar className="h-3 w-3" />
                        {new Date(bug.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditItem(bug); setShowAddModal(true); }}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(bug.id, 'bugs')}
                    className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
          {filteredBugs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Aucun bug signalé
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddEditModal
            type={activeTab}
            item={editItem}
            onSave={activeTab === 'changelog' ? handleSaveChangelog : handleSaveBug}
            onClose={() => { setShowAddModal(false); setEditItem(null); }}
            currentVersion={versionInfo?.version}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function AddEditModal({ type, item, onSave, onClose, currentVersion }) {
  const [formData, setFormData] = useState(
    item || (type === 'changelog' 
      ? { version: currentVersion, type: 'feature', title: '', description: '', status: 'completed', priority: 'medium' }
      : { title: '', description: '', status: 'open', priority: 'medium', resolution: '' }
    )
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Le titre est requis');
      return;
    }
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">
              {item ? 'Modifier' : 'Ajouter'} {type === 'changelog' ? 'une entrée' : 'un bug'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Titre *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={type === 'changelog' ? "Nouvelle fonctionnalité..." : "Description du bug..."}
            />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
              rows={3}
              placeholder="Détails..."
            />
          </div>

          {type === 'changelog' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Version</Label>
                  <Input
                    value={formData.version || ''}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="2.1.0"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="feature">Nouveauté</option>
                    <option value="bugfix">Correction</option>
                    <option value="improvement">Amélioration</option>
                    <option value="breaking">Breaking Change</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Statut</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {type === 'changelog' ? (
                  <>
                    <option value="planned">Planifié</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="cancelled">Annulé</option>
                  </>
                ) : (
                  <>
                    <option value="open">Ouvert</option>
                    <option value="in_progress">En cours</option>
                    <option value="resolved">Résolu</option>
                    <option value="closed">Fermé</option>
                    <option value="wontfix">Won't Fix</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <Label>Priorité</Label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="critical">Critique</option>
              </select>
            </div>
          </div>

          {type === 'bugs' && formData.status === 'resolved' && (
            <div>
              <Label>Résolution</Label>
              <textarea
                value={formData.resolution || ''}
                onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                rows={2}
                placeholder="Comment le bug a été résolu..."
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="submit">
              {item ? 'Mettre à jour' : 'Ajouter'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
